import { Switch, Route, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import ErrorFallback from './components/ErrorFallback/ErrorFallback';
import { AnimatePresence } from 'framer-motion';

import './App.css';
import Home from './pages/Home/Home';
import MultiPlayerCount from './pages/MultiPlayerCount/MultiPlayerCount';
import MultiPlayerPreRoll from './pages/MultiPlayerPreRoll/MultiPlayerPreRoll';
import MultiPlayerPostRoll from './pages/MultiPlayerPostRoll/MultiPlayerPostRoll';
import PlayerNames from './pages/PlayerNames/PlayerNames';
import Stats from './pages/Stats/Stats';
import TiebreakerPreRoll from './pages/TiebreakerPreRoll/TiebreakerPreRoll';
import TiebreakerPostRoll from './pages/TiebreakerPostRoll/TiebreakerPostRoll';
import SinglePlayerGames from './pages/SinglePlayerGames/SinglePlayerGames';
import Corebound from './pages/Corebound/Corebound';
import CursedSlotMachine from './pages/CursedSlotMachine/CursedSlotMachine';
import DiceDungeon from './pages/DiceDungeon/DiceDungeon';
import WarpTransitionProvider from './components/WarpTransition/WarpTransitionProvider';
import WarpRouteStage from './components/WarpTransition/WarpRouteStage';
import {
  getRandomMultiplayerRoll,
  getWinner,
  getTiebreakerWinner,
  initializeSounds,
  preloadSounds,
} from './utils/utils';
import { ErrorBoundary } from 'react-error-boundary';

const App = () => {
  const [playerNamesState, setPlayerNamesState] = useState([]);
  const [tiebreakerState, setTiebreakerState] = useState([]);
  const [isNameFormValid, setIsNameFormValid] = useState(true);
  const location = useLocation();

  useEffect(() => {
    const cleanup = initializeSounds();
    preloadSounds();
    return cleanup;
  }, []);

  const initializeUsers = (number) => {
    const userArray = [];

    for (let i = 1; i < number + 1; i++) {
      userArray.push({
        name: `Player ${i}`,
        rolls: [],
        average: 0,
        totalWins: 0,
        id: i,
      });
    }

    setPlayerNamesState(userArray);
  };

  const initializeTiebreakerPlayers = (tieBreakerPlayers) => {
    setTiebreakerState(tieBreakerPlayers);
  };

  const updatePlayerName = (id, updatedName) => {
    const updatedPlayerNameState = playerNamesState.map((player) => {
      if (player.id !== id) {
        return player;
      }
      return { ...player, name: updatedName };
    });
    const allNamesFilled = updatedPlayerNameState.every(
      (player) => player.name.trim() !== ''
    );
    setIsNameFormValid(allNamesFilled);

    setPlayerNamesState(updatedPlayerNameState);
  };

  const deletePlayer = (id) => {
    const filteredPlayerNameState = playerNamesState.filter(
      (playerNames) => playerNames.id !== id
    );
    setPlayerNamesState(filteredPlayerNameState);
  };

  const addPlayer = () => {
    const maxID = playerNamesState[playerNamesState.length - 1]?.id || 0;
    const newID = maxID + 1;
    const newPlayer = {
      name: `Player ${newID}`,
      rolls: [],
      average: 0,
      totalWins: 0,
      id: newID,
    };

    setPlayerNamesState([...playerNamesState, newPlayer]);
  };

  const addNewRolls = () => {
    const updateRolls = playerNamesState.map((player) => {
      const newRoll = getRandomMultiplayerRoll();
      const newRolls = [...player.rolls, newRoll];

      const average = (array) => array.reduce((a, b) => a + b) / array.length;

      return {
        ...player,
        rolls: newRolls,
        average: average(newRolls),
      };
    });

    let winningID = getWinner(updateRolls);
    winningID = winningID[2];

    const updatedPlayers = updateRolls.map((player) => {
      if (player.id === winningID) {
        return {
          ...player,
          totalWins: player.totalWins + 1,
        };
      }
      return player;
    });

    setPlayerNamesState(updatedPlayers);
  };

  const handleTiebreaker = () => {
    const updateTiebreakerRolls = tiebreakerState.map((player) => {
      const newRoll = getRandomMultiplayerRoll();
      const newRolls = [...player.rolls, newRoll];

      const average = (array) => array.reduce((a, b) => a + b) / array.length;

      return {
        ...player,
        rolls: newRolls,
        average: average(newRolls),
      };
    });

    let winningID = getTiebreakerWinner(updateTiebreakerRolls);
    winningID = winningID[2];

    let winner = updateTiebreakerRolls.find(
      (person) => person.id === winningID
    );
    if (winner) {
      winner.totalWins++;
    }

    setTiebreakerState(updateTiebreakerRolls);

    const updatePlayerRolls = playerNamesState.map((player) => {
      const playerInTiebreaker = updateTiebreakerRolls.find(
        (tiebreakerPlayer) => tiebreakerPlayer.id === player.id
      );

      return !playerInTiebreaker ? player : playerInTiebreaker;
    });

    setPlayerNamesState(updatePlayerRolls);
  };

  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onReset={() => {
        // optional: navigate home on reset
        window.location.href = '/';
      }}
    >
      <WarpTransitionProvider>
        <WarpRouteStage location={location}>
          {(visibleLocation) => (
            <AnimatePresence exitBeforeEnter>
              <Switch location={visibleLocation} key={visibleLocation.pathname}>
                <Route exact path="/single-player">
                  <SinglePlayerGames />
                </Route>

                <Route path="/single-player/corebound">
                  <Corebound />
                </Route>

                <Route path="/single-player/cursed-slot-machine">
                  <CursedSlotMachine />
                </Route>

                <Route path="/single-player/dice-dungeon">
                  <DiceDungeon />
                </Route>

                <Route path="/multi/select-player-count">
                  <MultiPlayerCount selectPlayerCount={initializeUsers} />
                </Route>

                <Route path="/multi/pre-roll">
                  <MultiPlayerPreRoll
                    addNewRolls={addNewRolls}
                    playerNamesState={playerNamesState}
                  />
                </Route>

                <Route path="/multi/post-roll">
                  <MultiPlayerPostRoll
                    playerNamesState={playerNamesState}
                    initializeTiebreakerPlayers={initializeTiebreakerPlayers}
                  />
                </Route>

                <Route path="/multi/player-names">
                  <PlayerNames
                    playerNamesState={playerNamesState}
                    updatePlayerName={updatePlayerName}
                    deletePlayer={deletePlayer}
                    addPlayer={addPlayer}
                    isNameFormValid={isNameFormValid}
                  />
                </Route>

                <Route path="/multi/stats">
                  <Stats playerNamesState={playerNamesState} />
                </Route>

                <Route path="/multi/tiebreaker/pre-roll">
                  <TiebreakerPreRoll
                    handleTiebreaker={handleTiebreaker}
                    tiebreakerState={tiebreakerState}
                    playerNamesState={playerNamesState}
                  />
                </Route>

                <Route path="/multi/tiebreaker/post-roll">
                  <TiebreakerPostRoll
                    tiebreakerState={tiebreakerState}
                    initializeTiebreakerPlayers={initializeTiebreakerPlayers}
                    playerNamesState={playerNamesState}
                  />
                </Route>

                <Route path="/">
                  <Home setPlayerNamesState={setPlayerNamesState} />
                </Route>
              </Switch>
            </AnimatePresence>
          )}
        </WarpRouteStage>
      </WarpTransitionProvider>
    </ErrorBoundary>
  );
};

export default App;
